import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import clientImg1 from '../assets/bathroom-client-1.jpg';
import clientImg2 from '../assets/bathroom-client-2.jpg';
import clientImg3 from '../assets/bathroom-client-3.jpg';
import clientImg4 from '../assets/bathroom-client-4.jpg';
import bathroomFeatureImg from '../assets/bathroom-feature.jpg';
import bathroomHeroLuxury from '../assets/bathroom-hero-luxury.jpg';

const Bathrooms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Bathroom Cabinetry | Luxe Cabinetry";
  }, []);

  const galleryImages = [
    clientImg1,
    clientImg2,
    clientImg3,
    clientImg4
  ];

  const features = [
    'Floating vanities',
    'Double sink configurations',
    'Moisture-resistant materials',
    'Custom mirrors'
  ];

  const processSteps = ['Consultation', 'Design Development', 'Craftsmanship', 'Installation'];

  return (
    <div className="pt-0">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden bg-primary">
        <div className="absolute inset-0 z-0">
          <img
            src={bathroomHeroLuxury}
            alt="Luxury Bathroom"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-primary/70 md:bg-primary/80" />
        </div>
        <div className="container-custom relative z-20 text-center text-white px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-2xl md:text-6xl font-bold mb-6 leading-snug md:leading-tight">
              Bathroom Cabinetry Designed <br className="hidden md:block" /> for Relaxation and Style
            </h1>
            <p className="text-lg md:text-2xl text-gray-200 font-light mb-10 leading-relaxed">
              Transform your daily routine into a spa-like experience with custom cabinetry.
            </p>
            <a
              href="#contact"
              className="bg-accent hover:bg-orange-600 text-white px-8 py-4 uppercase tracking-[0.2em] text-sm font-bold transition-all shadow-lg hover:shadow-orange-500/20 inline-block w-full md:w-auto rounded-lg"
            >
              Schedule Your Design Consultation
            </a>
          </motion.div>
        </div>
      </section>

      {/* Client Image Gallery */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            {galleryImages.map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative group overflow-hidden shadow-sm h-[400px] md:h-[500px]"
              >
                <img
                  src={img}
                  alt={`Custom Bathroom Project ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                />
                {/* No overlay, no buttons */}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Approach */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8">Our Approach to Bathroom Design</h2>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed font-light">
              We create bathroom environments that blend functionality with serenity. Our designs maximize space while providing a luxurious aesthetic, ensuring every element serves a purpose in your personal sanctuary.
            </p>
            <div className="w-24 h-1 bg-accent mx-auto mt-12" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="order-1 md:order-1">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary">Precision Features</h2>
              <ul className="space-y-6">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center text-lg text-gray-700 font-light border-b border-gray-100 pb-4">
                    <Check size={20} className="text-accent mr-4" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="h-[300px] md:h-[500px] bg-gray-200 relative overflow-hidden shadow-2xl order-2 md:order-2">
              <img
                src={bathroomFeatureImg}
                alt="Bathroom Features"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Snapshot */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Our Process</h2>
            <div className="w-24 h-1 bg-accent mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 rounded-full bg-gray-100 text-primary flex items-center justify-center text-xl font-bold mx-auto mb-6 group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                  {index + 1}
                </div>
                <h3 className="text-lg font-bold text-primary uppercase tracking-wider">{step}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-32 bg-neutral relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
           <img src="/assets/youware-bg.png" className="w-full h-full object-cover" alt="pattern" />
        </div>
        <div className="container-custom relative z-10 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-primary mb-8">Ready to Transform Your Space?</h2>
          <a
            href="#contact"
            className="bg-primary text-white hover:bg-accent px-10 py-5 uppercase tracking-[0.2em] text-sm font-bold transition-all shadow-xl inline-flex items-center justify-center gap-3 w-full md:w-auto"
          >
            Schedule Your Design Consultation
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Bathrooms;
